/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../services/apiClient';

// Performance optimization utilities
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const useVirtualization = (items, containerHeight, itemHeight) => {
  const [scrollTop, setScrollTop] = useState(0);
  
  const visibleItems = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      items.length
    );
    
    return {
      startIndex,
      endIndex,
      visibleItems: items.slice(startIndex, endIndex),
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight
    };
  }, [items, scrollTop, containerHeight, itemHeight]);

  return {
    ...visibleItems,
    onScroll: (e) => setScrollTop(e.target.scrollTop)
  };
};

const useLazyLoading = (threshold = 0.1) => {
  const [ref, setRef] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold }
    );

    observer.observe(ref);

    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [ref, threshold]);

  return [setRef, isVisible];
};

// Memoized components
const MemoizedTableRow = React.memo(({ item, columns, onEdit, onDelete, language }) => {
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {columns.map((column, index) => (
        <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {typeof column.render === 'function' ? column.render(item) : item[column.key]}
        </td>
      ))}
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(item)}
            className="text-blue-600 hover:text-blue-900 transition-colors"
          >
            {language === 'ar' ? 'تعديل' : 'Edit'}
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="text-red-600 hover:text-red-900 transition-colors"
          >
            {language === 'ar' ? 'حذف' : 'Delete'}
          </button>
        </div>
      </td>
    </tr>
  );
});

const VirtualizedTable = React.memo(({ 
  data, 
  columns, 
  onEdit, 
  onDelete, 
  language,
  containerHeight = 400,
  itemHeight = 60 
}) => {
  const {
    visibleItems,
    totalHeight,
    offsetY,
    onScroll
  } = useVirtualization(data, containerHeight, itemHeight);

  return (
    <div className="virtualized-table-container">
      <div
        className="overflow-auto border border-gray-200 rounded-lg"
        style={{ height: containerHeight }}
        onScroll={onScroll}
      >
        <div style={{ height: totalHeight, position: 'relative' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.title}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {language === 'ar' ? 'الإجراءات' : 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody 
              className="bg-white divide-y divide-gray-200"
              style={{ transform: `translateY(${offsetY}px)` }}
            >
              {visibleItems.map((item, index) => (
                <MemoizedTableRow
                  key={item.id || index}
                  item={item}
                  columns={columns}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  language={language}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

const LazyLoadedCard = React.memo(({ children, className = "" }) => {
  const [ref, isVisible] = useLazyLoading();
  
  return (
    <div ref={ref} className={`lazy-card ${className}`}>
      {isVisible ? children : (
        <div className="animate-pulse bg-gray-200 rounded-lg h-32 flex items-center justify-center">
          <div className="text-gray-400">Loading...</div>
        </div>
      )}
    </div>
  );
});

const OptimizedSearch = React.memo(({ onSearch, placeholder, language }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    onSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearch]);

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder={placeholder}
      />
    </div>
  );
});

const PerformanceMonitor = ({ children }) => {
  const [metrics, setMetrics] = useState({
    renderTime: 0,
    memoryUsage: 0,
    componentCount: 0
  });

  useEffect(() => {
    const startTime = performance.now();
    
    // Measure render time
    const measureRenderTime = () => {
      const endTime = performance.now();
      setMetrics(prev => ({
        ...prev,
        renderTime: endTime - startTime
      }));
    };

    // Measure memory usage (if available)
    if (performance.memory) {
      setMetrics(prev => ({
        ...prev,
        memoryUsage: performance.memory.usedJSHeapSize / 1024 / 1024 // MB
      }));
    }

    measureRenderTime();
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_ENABLE_PERFORMANCE_LOGS === 'true') {
      console.debug('Performance Metrics:', metrics);
    }
  }, [metrics]);

  return children;
};

const CacheProvider = ({ children }) => {
  const [cache, setCache] = useState(new Map());

  const cacheGet = useCallback((key) => {
    return cache.get(key);
  }, [cache]);

  const cacheSet = useCallback((key, value, ttl = 5 * 60 * 1000) => { // 5 minutes default TTL
    const expirationTime = Date.now() + ttl;
    setCache(prev => new Map(prev.set(key, { value, expirationTime })));
  }, []);

  const cacheDelete = useCallback((key) => {
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(key);
      return newCache;
    });
  }, []);

  const cacheClear = useCallback(() => {
    setCache(new Map());
  }, []);

  // Clean expired entries
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCache(prev => {
        const newCache = new Map();
        for (const [key, entry] of prev.entries()) {
          if (entry.expirationTime > now) {
            newCache.set(key, entry);
          }
        }
        return newCache;
      });
    }, 60000); // Clean every minute

    return () => clearInterval(interval);
  }, []);

  const cacheContext = {
    get: cacheGet,
    set: cacheSet,
    delete: cacheDelete,
    clear: cacheClear
  };

  return (
    <CacheContext.Provider value={cacheContext}>
      {children}
    </CacheContext.Provider>
  );
};

const CacheContext = React.createContext();

const useCache = () => {
  const context = React.useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

// Optimized data fetching hook
const useOptimizedFetch = (url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cache = useCache();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check cache first
      const cacheKey = `${url}_${JSON.stringify(options)}`;
      const cachedData = cache.get(cacheKey);
      
      if (cachedData) {
        setData(cachedData.value);
        setLoading(false);
        return;
      }

      const response = await apiClient.request({
        url,
        method: options.method || 'get',
        data: options.body,
        headers: options.headers,
        params: options.params,
        skipAuthRedirect: options.skipAuthRedirect,
        responseType: options.responseType || 'json',
        withCredentials: options.withCredentials,
      });
      
      const result = response.data;
      
      // Cache the result
      cache.set(cacheKey, result);
      
      setData(result);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [url, options, cache]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Image optimization component
const OptimizedImage = React.memo(({ 
  src, 
  alt, 
  width, 
  height, 
  className = "",
  lazy = true,
  quality = 80 
}) => {
  const [imageRef, isVisible] = useLazyLoading();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    if (isVisible || !lazy) {
      // Generate optimized image URL based on requirements
      const optimizedSrc = generateOptimizedImageUrl(src, { width, height, quality });
      setImageSrc(optimizedSrc);
    }
  }, [isVisible, lazy, src, width, height, quality]);

  const generateOptimizedImageUrl = (originalSrc, options) => {
    // In a real implementation, this would integrate with image optimization services
    // For now, we'll just return the original source
    return originalSrc;
  };

  return (
    <div ref={imageRef} className={`optimized-image ${className}`}>
      {(isVisible || !lazy) && imageSrc ? (
        <>
          {!imageLoaded && (
            <div className="animate-pulse bg-gray-200 rounded" style={{ width, height }}>
              <div className="h-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          )}
          <img
            src={imageSrc}
            alt={alt}
            width={width}
            height={height}
            className={`${imageLoaded ? 'block' : 'hidden'} transition-opacity duration-300`}
            onLoad={() => setImageLoaded(true)}
            loading={lazy ? 'lazy' : 'eager'}
          />
        </>
      ) : (
        <div className="bg-gray-200 rounded animate-pulse" style={{ width, height }} />
      )}
    </div>
  );
});

export {
  useDebounce,
  useVirtualization,
  useLazyLoading,
  useCache,
  useOptimizedFetch,
  VirtualizedTable,
  LazyLoadedCard,
  OptimizedSearch,
  PerformanceMonitor,
  CacheProvider,
  OptimizedImage,
  MemoizedTableRow
};